import { suggestedCategory, type Transaction } from "@keel/core";
import { withLockConfirm } from '@/lib/locked';
import { skipToken, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import {
  ArrowLeftRight,
  Check,
  CheckCheck,
  FileUp,
  Hash,
  Maximize2,
  Paperclip,
  Plus,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { errorMessage, useTRPC, useTRPCClient } from "@/api/query";
import {
  Badge,
  Button,
  Combobox,
  DataTable,
  DateInput,
  Dialog,
  DialogContent,
  EmptyState,
  IconButton,
  Input,
  Kbd,
  MoneyInput,
  MoneyText,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@/components/ui";
import { useDates } from "@/lib/dates";
import { PHONE, useMediaQuery } from "@/lib/media";
import { useHotkeys } from "@/lib/keyboard";
import { categoryOptions, useCreateCategory, useLedger, useRefresh } from "./data";
import {
  EMPTY_FILTER,
  activeFilterCount,
  toListInput,
  type TxFilter,
} from "./filters";
import { plural } from "./labels";
import { QuickAddBar } from "./QuickAddBar";
import { QueryError, StatusChip, TableSkeleton } from "./states";
import { TransactionEditor, type EditorDefaults } from "./TransactionEditor";
import { useTxActions } from "./tx-actions";
import { TxFilterBar } from "./TxFilterBar";
import { BRAND } from '@/brand';

export interface RegisterProps {
  /** Lock the register to one account (account detail): hides the account filter, shows the running balance. */
  readonly accountId?: string;
  /** Embedded inside another page: no page-level saved filters, compact header. */
  readonly embedded?: boolean;
}

type Row = Transaction & { runningBalanceCents?: number };

export type RegisterAction =
  "select" | "edit" | "categorise" | "review" | "delete" | "duplicate" | "new";

/**
 * Register keymap (rows handle j/k/↑/↓/Home/End/Enter/Space themselves via DataTable):
 * `x` select · `e` full editor · `c` categorise · `r` mark reviewed · `d` delete (Undo) · `⌘D` duplicate · `n` new.
 */
export const registerKeyAction = (
  e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">,
): RegisterAction | null => {
  const mod = e.metaKey || e.ctrlKey;
  if (e.altKey) return null;
  const k = e.key.toLowerCase();
  if (mod) return k === "d" ? "duplicate" : null;
  if (e.shiftKey) return null;
  return (
    (
      {
        x: "select",
        e: "edit",
        c: "categorise",
        r: "review",
        d: "delete",
        n: "new",
      } as const
    )[k as "x"] ?? null
  );
};

interface InlineDraft {
  readonly date: string;
  readonly payee: string;
  readonly categoryId: string | null;
  readonly amountCents: number | null;
}

interface InlineApi {
  readonly draft: InlineDraft;
  readonly set: (patch: Partial<InlineDraft>) => void;
  readonly onKey: (e: KeyboardEvent<HTMLElement>) => void;
  readonly save: () => void;
  readonly cancel: () => void;
  readonly openFull: () => void;
  readonly currency: string | undefined;
  readonly categoryOptions: ReturnType<typeof categoryOptions>;
}
/** Inline-edit state lives in context so typing doesn't rebuild the column defs (which would remount the inputs). */
const InlineContext = createContext<InlineApi | null>(null);
const useInline = (): InlineApi => {
  const v = useContext(InlineContext);
  if (!v) throw new Error("Inline editor outside the register");
  return v;
};

const InlineDate = ({ txId }: { readonly txId: string }) => {
  const i = useInline();
  return (
    <div onKeyDown={i.onKey}>
      <DateInput
        aria-label="Date"
        value={i.draft.date}
        onChange={(e) => i.set({ date: e.target.value })}
        className="h-9 w-36"
      />
      <span data-txid={txId} hidden />
    </div>
  );
};
const InlinePayee = () => {
  const i = useInline();
  return (
    // Save/Cancel sit beside the payee (always on screen), not at the far right where wide tables push them off.
    <div onKeyDown={i.onKey} className="flex items-center gap-1">
      <Input
        aria-label="Payee"
        autoFocus
        value={i.draft.payee}
        onChange={(e) => i.set({ payee: e.target.value })}
        className="h-9 min-w-[10rem] flex-1"
        list="register-payees"
      />
      <IconButton aria-label="Save" icon={<Check aria-hidden />} onClick={i.save} />
      <IconButton aria-label="Cancel" icon={<X aria-hidden />} onClick={i.cancel} />
    </div>
  );
};
/** Save/Cancel for an edited transfer leg (its payee isn't editable inline). */
const InlineActions = ({ label }: { readonly label: string }) => {
  const i = useInline();
  return (
    <div onKeyDown={i.onKey} className="flex items-center gap-1">
      <span className="min-w-0 flex-1 truncate text-text">{label}</span>
      <IconButton aria-label="Save" icon={<Check aria-hidden />} onClick={i.save} />
      <IconButton aria-label="Cancel" icon={<X aria-hidden />} onClick={i.cancel} />
    </div>
  );
};
const InlineCategory = () => {
  const i = useInline();
  const createCategory = useCreateCategory((i.draft.amountCents ?? -1) > 0 ? "income" : "expense");
  return (
    <div onKeyDown={i.onKey} className="min-w-[10rem]">
      <Combobox
        aria-label="Category"
        value={i.draft.categoryId}
        options={i.categoryOptions}
        onCreate={createCategory}
        placeholder="Uncategorised"
        onChange={(v) => i.set({ categoryId: v })}
        className="h-9"
      />
    </div>
  );
};
const InlineAmount = () => {
  const i = useInline();
  return (
    <div onKeyDown={i.onKey} className="flex items-center justify-end gap-1">
      <MoneyInput
        aria-label="Amount"
        value={i.draft.amountCents}
        currency={i.currency}
        onChange={(c) => i.set({ amountCents: c })}
        className="h-9 w-32"
      />
      <IconButton
        aria-label="Open full editor"
        icon={<Maximize2 aria-hidden />}
        onClick={i.openFull}
      />
    </div>
  );
};

const isTyping = (t: EventTarget | null) =>
  t instanceof HTMLElement &&
  (t.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) ||
    t.getAttribute("role") === "combobox");

/**
 * The transaction register: filters, quick add, a keyboard-first table with running balance (single account),
 * inline edit (Enter or click), the full editor (`e`), bulk actions and palette params (`?new=1`, `?import=1`, `?focus=`).
 */
export const Register = ({
  accountId: lockedAccountId,
  embedded,
}: RegisterProps) => {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const refresh = useRefresh();
  const actions = useTxActions();
  const dates = useDates();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const ledger = useLedger();
  const {
    entityId,
    entity,
    accountById,
    categoryById,
    payeeById,
    tagById,
    categories,
    tags,
    canEdit,
    openAccounts,
  } = ledger;
  const [filter, setFilter] = useState<TxFilter>(() => ({
    ...EMPTY_FILTER,
    ...(params.get("account")
      ? { accountId: params.get("account") ?? undefined }
      : {}),
    // ?from=&to= (a statement period, say) opens on that custom range
    ...(params.get("from") || params.get("to")
      ? { preset: "custom" as const, from: params.get("from") ?? undefined, to: params.get("to") ?? undefined }
      : {}),
    q: params.get("q") ?? "",
    unreviewedOnly: params.get("unreviewed") === "1",
  }));
  const effective: TxFilter = lockedAccountId
    ? { ...filter, accountId: lockedAccountId }
    : filter;
  const singleAccount = effective.accountId;
  const input = entityId
    ? toListInput(
        entityId,
        effective,
        dates.today,
        entity?.fyStartMonth,
        entity?.fyStartDay,
      )
    : skipToken;
  const list = useInfiniteQuery(
    trpc.transactions.list.infiniteQueryOptions(input, {
      getNextPageParam: (p) => p.nextCursor,
    }),
  );
  const unreviewed = useQuery(
    trpc.transactions.unreviewedCount.queryOptions(
      entityId ? { entityId } : skipToken,
      { meta: { silent: true } },
    ),
  );
  const uncategorised = useQuery(
    trpc.transactions.uncategorisedCount.queryOptions(
      entityId && canEdit ? { entityId } : skipToken,
      { meta: { silent: true } },
    ),
  );
  const transferHints = useQuery(
    trpc.transactions.transferSuggestions.queryOptions(
      entityId && canEdit ? { entityId } : skipToken,
      { meta: { silent: true } },
    ),
  );
  /** Row id → the other row of a suggested transfer pair ("Looks like a transfer to ··7697 — Link?"). */
  const pairOf = useMemo(() => {
    const m = new Map<string, { outId: string; inId: string; otherId: string }>();
    for (const p of transferHints.data ?? []) {
      m.set(p.outId, { ...p, otherId: p.inId });
      m.set(p.inId, { ...p, otherId: p.outId });
    }
    return m;
  }, [transferHints.data]);
  const [autoRunning, setAutoRunning] = useState(false);
  const rows: Row[] = useMemo(
    () => list.data?.pages.flatMap((p) => p.items) ?? [],
    [list.data],
  );
  const total = list.data?.pages[0]?.total ?? rows.length;
  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const [selection, setSelection] = useState<RowSelectionState>({});
  const selected = rows.filter((r) => selection[r.id]);
  const [editor, setEditor] = useState<{
    open: boolean;
    tx: Transaction | null;
    defaults: EditorDefaults;
  }>({ open: false, tx: null, defaults: {} });
  const [inline, setInline] = useState<{
    id: string;
    draft: InlineDraft;
  } | null>(null);
  const [categorising, setCategorising] = useState<Row[] | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const quickAdd = useRef<HTMLInputElement>(null);
  const refocus = useRef<number | null>(null);

  const defaultAccountId = openAccounts.find((a) => a.type === "transaction")?.id ?? openAccounts[0]?.id;
  const openNew = useCallback(
    () =>
      setEditor({
        open: true,
        tx: null,
        // the account quick add uses (or the first everyday account), so "Account" isn't a blank required field
        defaults: { accountId: singleAccount ?? defaultAccountId },
      }),
    [singleAccount, defaultAccountId],
  );
  const openEdit = (t: Transaction) => {
    setInline(null);
    setEditor({ open: true, tx: t, defaults: {} });
  };

  // Palette / deep-link params (top-level register only).
  useEffect(() => {
    if (embedded) return;
    const next = new URLSearchParams(params);
    let changed = false;
    if (params.get("import") === "1") {
      navigate("/transactions/import", { replace: true });
      return;
    }
    if (params.get("new") === "1") {
      openNew();
      next.delete("new");
      changed = true;
    }
    const focus = params.get("focus");
    if (focus) {
      next.delete("focus");
      changed = true;
      client.transactions.get.query({ id: focus }).then(
        (t) => setEditor({ open: true, tx: t, defaults: {} }),
        (e) => toast.error(errorMessage(e)),
      );
    }
    if (changed) setParams(next, { replace: true });
  }, [params, embedded, navigate, openNew, setParams, client]);

  // Keep focus on the same row position after rows change (delete, categorise).
  useEffect(() => {
    if (refocus.current === null) return;
    const i = refocus.current;
    refocus.current = null;
    requestAnimationFrame(() => focusRow(i));
  }, [rows]);

  const rowEls = () =>
    wrapper.current?.querySelectorAll<HTMLTableRowElement>(
      "tbody tr[data-row]",
    ) ?? [];
  const focusRow = (i: number) => {
    const els = rowEls();
    els[Math.max(0, Math.min(els.length - 1, i))]?.focus();
  };
  const focusedRow = (
    target: EventTarget | null,
  ): { tx: Row; index: number } | null => {
    const tr =
      target instanceof HTMLElement ? target.closest("tr[data-row]") : null;
    const id = tr?.querySelector("[data-txid]")?.getAttribute("data-txid");
    const tx = id ? byId.get(id) : undefined;
    return tx && tr
      ? { tx, index: Array.from(rowEls()).indexOf(tr as HTMLTableRowElement) }
      : null;
  };

  const run = (
    action: RegisterAction,
    target: { tx: Row; index: number } | null,
  ) => {
    const targets = selected.length ? selected : target ? [target.tx] : [];
    switch (action) {
      case "new":
        if (canEdit) openNew();
        return true;
      case "select":
        if (!target) return false;
        setSelection((s) => ({ ...s, [target.tx.id]: !s[target.tx.id] }));
        return true;
      case "edit":
        if (!target) return false;
        openEdit(target.tx);
        return true;
      default:
    }
    if (!canEdit || !targets.length) return false;
    if (target) refocus.current = target.index;
    if (action === "categorise") setCategorising(targets);
    if (action === "review") void actions.markReviewed(targets);
    if (action === "delete") {
      setSelection({});
      void actions.remove(targets);
    }
    if (action === "duplicate" && target) void actions.duplicate(target.tx);
    return true;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || inline || isTyping(e.target)) return;
    const action = registerKeyAction(e);
    if (!action) return;
    if (run(action, focusedRow(e.target))) e.preventDefault();
  };

  // From anywhere on the page: j/k start keyboard navigation, q focuses quick add.
  useHotkeys(
    {
      j: () =>
        wrapper.current?.contains(document.activeElement) ? false : focusRow(0),
      k: () =>
        wrapper.current?.contains(document.activeElement) ? false : focusRow(0),
      q: () => quickAdd.current?.focus(),
    },
    { enabled: !embedded },
  );
  // `n` anywhere on a page with a register opens its editor (the global `n` defers to it via [data-register])
  useHotkeys({ n: () => (canEdit ? openNew() : false) });

  // ───────── inline edit ─────────
  // A changed draft is saved, never silently dropped, when the user moves on (another row, or a click elsewhere).
  const inlineDirty = (): boolean => {
    if (!inline) return false;
    const t = byId.get(inline.id);
    if (!t) return false;
    const d = inline.draft;
    const shown = (t.payeeId ? payeeById.get(t.payeeId)?.name : undefined) ?? t.payeeRaw;
    return d.date !== t.date || d.amountCents !== t.amountCents || d.payee.trim() !== shown.trim()
      || (t.splits.length === 1 && d.categoryId !== (t.splits[0]?.categoryId ?? null));
  };
  const startInline = async (t: Row) => {
    if (inline && inline.id !== t.id && inlineDirty()) await saveInline();
    if (!canEdit || t.status === "reconciled") {
      openEdit(t);
      return;
    }
    const payee =
      (t.payeeId ? payeeById.get(t.payeeId)?.name : undefined) ?? t.payeeRaw;
    setInline({
      id: t.id,
      draft: {
        date: t.date,
        payee,
        categoryId: t.splits[0]?.categoryId ?? null,
        amountCents: t.amountCents,
      },
    });
  };
  const setDraft = (patch: Partial<InlineDraft>) =>
    setInline((s) => (s ? { ...s, draft: { ...s.draft, ...patch } } : s));
  const saveInline = async (andNew = false) => {
    if (!inline) return;
    const t = byId.get(inline.id);
    const d = inline.draft;
    if (!t || d.amountCents === null || d.amountCents === 0) {
      toast.error("Enter an amount.");
      return;
    }
    const amountCents = d.amountCents;
    const index = Array.from(rowEls()).findIndex((r) =>
      r.querySelector(`[data-txid="${t.id}"]`),
    );
    try {
      const match = ledger.payees.find(
        (p) => p.name.toLowerCase() === d.payee.trim().toLowerCase(),
      );
      // Unchanged payee: keep the bank's raw text (payeeRaw) and the payee link as they are.
      const shown = (t.payeeId ? payeeById.get(t.payeeId)?.name : undefined) ?? t.payeeRaw;
      const payeeChanged = d.payee.trim() !== shown.trim();
      const saved = await withLockConfirm((flag) => client.transactions.update.mutate({
        ...flag,
        id: t.id,
        date: d.date,
        amountCents,
        ...(t.transferId
          ? {}
          : {
              ...(payeeChanged
                ? { payeeRaw: d.payee.trim(), payeeId: match?.id ?? null }
                : {}),
              ...(t.splits.length === 1
                ? {
                    splits: [
                      {
                        ...t.splits[0],
                        categoryId: d.categoryId,
                        amountCents,
                      },
                    ],
                  }
                : {}),
            }),
        isReviewed: true,
      }));
      setInline(null);
      await refresh();
      actions.announceLearned(t.entityId, saved.learned);
      if (andNew) openNew();
      else requestAnimationFrame(() => focusRow(index));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const onInlineKey = (e: KeyboardEvent<HTMLElement>) => {
    e.stopPropagation(); // keep j/k/space for typing
    const inPopover =
      e.target instanceof HTMLElement && e.target.closest("[cmdk-root]");
    if (e.key === "Escape" && !inPopover) {
      const id = inline?.id;
      setInline(null);
      requestAnimationFrame(() => {
        const i = Array.from(rowEls()).findIndex((r) =>
          r.querySelector(`[data-txid="${id}"]`),
        );
        focusRow(i);
      });
    } else if (
      e.key === "Enter" &&
      !inPopover &&
      !(e.target instanceof HTMLButtonElement)
    ) {
      e.preventDefault();
      void saveInline(e.metaKey || e.ctrlKey);
    }
  };

  const outsideRef = useRef<() => void>(() => {});
  outsideRef.current = () => { if (inlineDirty()) void saveInline(); else setInline(null); };
  useEffect(() => {
    if (!inline) return;
    const onDown = (e: PointerEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (!el) return;
      // rows handle their own clicks; pickers render in portals (popovers, command lists, menus, dialogs)
      if (wrapper.current?.querySelector("tbody")?.contains(el)) return;
      if (el.closest("[data-radix-popper-content-wrapper],[cmdk-root],[role=listbox],[role=option],[role=dialog],[role=menu]")) return;
      outsideRef.current();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [inline?.id]);

  const catOpts = useMemo(() => categoryOptions(categories), [categories]);
  const editingId = inline?.id;
  const editingTx = editingId ? byId.get(editingId) : undefined;
  const inlineApi: InlineApi | null = inline
    ? {
        draft: inline.draft,
        set: setDraft,
        onKey: onInlineKey,
        save: () => void saveInline(),
        cancel: () => setInline(null),
        openFull: () => editingTx && openEdit(editingTx),
        currency: editingTx
          ? accountById.get(editingTx.accountId)?.currency
          : undefined,
        categoryOptions: catOpts,
      }
    : null;
  const columns = useMemo<ColumnDef<Row, unknown>[]>(() => {
    const cols: ColumnDef<Row, unknown>[] = [
      {
        id: "date",
        header: "Date",
        enableSorting: false,
        cell: ({ row: { original: t } }) =>
          t.id === editingId ? (
            <InlineDate txId={t.id} />
          ) : (
            <span
              data-txid={t.id}
              className="whitespace-nowrap tabular-nums text-text-muted"
            >
              {dates.format(t.date, "medium")}
            </span>
          ),
      },
      {
        id: "payee",
        header: "Payee",
        enableSorting: false,
        cell: ({ row: { original: t } }) => {
          if (t.id === editingId && !t.transferId) return <InlinePayee />;
          const other = t.transferAccountId
            ? accountById.get(t.transferAccountId)
            : undefined;
          const name = t.transferId
            ? `Transfer ${t.amountCents < 0 ? "to" : "from"} ${other?.name ?? "another account"}`
            : ((t.payeeId ? payeeById.get(t.payeeId)?.name : undefined) ??
              (t.payeeRaw || "No payee"));
          if (t.id === editingId) return <InlineActions label={name} />;
          return (
            // Capped width + wrap to two lines: long bank descriptions never push Amount/Balance off-screen.
            <div className="flex min-w-[8rem] max-w-[28rem] flex-wrap items-start gap-x-2 gap-y-1">
              <div className="min-w-[7rem] flex-1">
                <p
                  title={name}
                  className={
                    t.isReviewed
                      ? "line-clamp-2 break-words text-text"
                      : "line-clamp-2 break-words font-semibold text-text"
                  }
                >
                  {name}
                </p>
                {t.memo ? (
                  <p title={t.memo} className="truncate text-xs text-text-muted">{t.memo}</p>
                ) : null}
              </div>
              {!t.isReviewed ? <Badge tone="accent" className="shrink-0">To review</Badge> : null}
              {t.attachmentIds.length ? (
                <Paperclip
                  className="mt-0.5 size-3.5 shrink-0 text-text-muted"
                  aria-label="Has receipt"
                />
              ) : null}
            </div>
          );
        },
      },
      {
        id: "category",
        header: "Category",
        enableSorting: false,
        cell: ({ row: { original: t } }) => {
          const acct = (id: string | null | undefined) => {
            const a = id ? accountById.get(id) : undefined;
            if (!a) return "another account";
            const d = (a.accountNumberMasked ?? "").replace(/\D/g, "").slice(-4);
            return d && !a.name.includes(d) ? `${a.name} ··${d}` : a.name;
          };
          if (t.transferId || t.transferAccountId) {
            const [from, to] = t.amountCents < 0 ? [t.accountId, t.transferAccountId] : [t.transferAccountId, t.accountId];
            return (
              <div className="flex flex-wrap items-center gap-x-2 text-text-muted">
                <span>
                  {t.transferId
                    ? `Transfer · ${acct(from)} → ${acct(to)}`
                    : `Transfer ${t.amountCents < 0 ? "to" : "from"} ${acct(t.transferAccountId)} · waiting for the other side`}
                </span>
                {canEdit && t.status !== "reconciled" ? (
                  <button
                    type="button"
                    className="text-xs underline-offset-2 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      void actions.unlinkTransfer(t);
                    }}
                  >
                    Not a transfer
                  </button>
                ) : null}
              </div>
            );
          }
          if (t.id === editingId && t.splits.length === 1)
            return <InlineCategory />;
          const pair = pairOf.get(t.id);
          if (pair && canEdit && entityId) {
            const other = byId.get(pair.otherId);
            return (
              <button
                type="button"
                className="text-left text-sm text-text-muted underline-offset-2 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  void actions.linkTransfer(entityId, pair.outId, pair.inId);
                }}
              >
                Looks like a transfer {t.amountCents < 0 ? "to" : "from"} {acct(other?.accountId)} — Link?
              </button>
            );
          }
          const tagNames = [...new Set(t.splits.flatMap((s) => s.tagIds))]
            .map((id) => tagById.get(id)?.name)
            .filter(Boolean);
          const cat =
            t.splits.length > 1
              ? `${t.splits.length} splits`
              : t.splits[0]?.categoryId
                ? categoryById.get(t.splits[0].categoryId)?.name
                : undefined;
          const hint =
            !cat && t.splits.length === 1 && !t.isReviewed
              ? suggestedCategory(t, categories, entity?.country)
              : null;
          return (
            <div className="flex flex-wrap items-center gap-1">
              <span className={cat ? "text-text" : "text-text-muted"}>
                {cat ?? "Uncategorised"}
              </span>
              {hint ? (
                <span className="text-xs text-text-muted">
                  · Suggested: {hint.category.name}
                </span>
              ) : null}
              {tagNames.map((n) => (
                <Badge key={n}>#{n}</Badge>
              ))}
            </div>
          );
        },
      },
    ];
    if (!lockedAccountId) {
      cols.push({
        id: "account",
        header: "Account",
        enableSorting: false,
        cell: ({ row: { original: t } }) => (
          <span
            title={accountById.get(t.accountId)?.name}
            className="block max-w-[14rem] truncate text-text-muted"
          >
            {accountById.get(t.accountId)?.name ?? ""}
          </span>
        ),
      });
    }
    cols.push(
      {
        id: "amount",
        header: "Amount",
        enableSorting: false,
        meta: { align: "right" },
        cell: ({ row: { original: t } }) =>
          t.id === editingId ? (
            <InlineAmount />
          ) : (
            // status sits under the amount (cleared is the norm, so it shows nothing) — one column fewer
            <div className="flex flex-col items-end gap-0.5 whitespace-nowrap">
              <MoneyText
                cents={t.amountCents}
                currency={accountById.get(t.accountId)?.currency}
                tone="auto"
                className={
                  t.status === "void" ? "line-through opacity-60" : "font-medium"
                }
              />
              {t.status !== "cleared" ? <StatusChip status={t.status} /> : null}
            </div>
          ),
      },
    );
    if (singleAccount) {
      cols.push({
        id: "balance",
        header: "Balance",
        enableSorting: false,
        meta: { align: "right" },
        cell: ({ row: { original: t } }) =>
          t.runningBalanceCents !== undefined ? (
            <MoneyText
              cents={t.runningBalanceCents}
              currency={accountById.get(t.accountId)?.currency}
              className="text-text-muted"
            />
          ) : null,
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editingId,
    lockedAccountId,
    singleAccount,
    accountById,
    categoryById,
    payeeById,
    tagById,
    dates,
    pairOf,
    categories,
    entity?.country,
    canEdit,
  ]);

  // phones get a list (amount always visible) instead of the wide table
  const narrow = useMediaQuery(PHONE);
  // Space the register really has (window minus sidebar): a narrow pane gets the list, a mid one drops Account
  // (still under Columns), so Amount is never pushed off-screen.
  const [width, setWidth] = useState(Infinity);
  const roomy = width >= ACCOUNT_COLUMN_FROM;
  const mounted = !(list.isPending || ledger.loading);
  useEffect(() => {
    const el = wrapper.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setWidth(e!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [mounted]);
  const filtersActive = activeFilterCount(filter) > 0;

  const emptyState =
        openAccounts.length === 0 ? (
          <EmptyState
            illustration={<Plus />}
            title="Add an account to start your register"
            body={`Transactions live in accounts. Add your everyday bank account, or import a statement and ${BRAND.name} creates it for you.`}
            action={
              <Button asChild>
                <Link to="/accounts?new=1">Add an account</Link>
              </Button>
            }
            secondary={
              <Button
                asChild
                variant="outline"
                icon={<FileUp aria-hidden />}
              >
                <Link to="/transactions/import">Import a file</Link>
              </Button>
            }
            helpKey="transactions"
          />
        ) : filtersActive ? (
          <EmptyState
            compact
            title="Nothing matches these filters"
            body="Try a wider date range or clear a filter."
            action={
              <Button
                variant="outline"
                onClick={() => setFilter({ ...EMPTY_FILTER })}
              >
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            illustration={<Plus />}
            title="No transactions yet"
            body='Type one into quick add, like "coffee 4.50", or import a bank statement to bring in months at once.'
            action={
              canEdit ? (
                <Button onClick={openNew}>New transaction</Button>
              ) : undefined
            }
            secondary={
              <Button
                asChild
                variant="outline"
                icon={<FileUp aria-hidden />}
              >
                <Link
                  to={`/transactions/import${singleAccount ? `?account=${singleAccount}` : ""}`}
                >
                  Import a file
                </Link>
              </Button>
            }
            helpKey="transactions.new"
          />
        )
      ;

  if (list.isError)
    return (
      <QueryError
        error={list.error}
        onRetry={() => void list.refetch()}
        title="Transactions couldn't be loaded"
      />
    );

  return (
    <div className="flex flex-col gap-4" data-register>
      {canEdit && (openAccounts.length > 0 || ledger.loading) ? (
        <QuickAddBar
          ref={quickAdd}
          accountId={singleAccount}
          lockAccount={Boolean(lockedAccountId)}
        />
      ) : null}
      <TxFilterBar
        filter={filter}
        onChange={setFilter}
        lockedAccountId={lockedAccountId}
        saved={!embedded}
        unreviewedCount={unreviewed.data}
      />
      {canEdit && entityId && (uncategorised.data ?? 0) >= 5 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 text-sm">
          <p className="text-text">
            {plural(uncategorised.data ?? 0, "transaction")} still uncategorised.{" "}
            <span className="text-text-muted">
              {BRAND.name} can name payees, match transfers between your accounts and fill in the categories it's sure about. You can undo it.
            </span>
          </p>
          <Button
            size="sm"
            loading={autoRunning}
            onClick={() => {
              setAutoRunning(true);
              void actions.autoCategorise(entityId).finally(() => setAutoRunning(false));
            }}
          >
            Categorise automatically
          </Button>
        </div>
      ) : null}
      {selected.length ? (
        <BulkToolbar
          rows={selected}
          onClear={() => setSelection({})}
          onCategorise={() => setCategorising(selected)}
          onDelete={() => {
            setSelection({});
            void actions.remove(selected);
          }}
        />
      ) : null}
      <datalist id="register-payees">
        {ledger.payees.map((p) => (
          <option key={p.id} value={p.name} />
        ))}
      </datalist>
      {list.isPending || ledger.loading ? (
        <TableSkeleton />
      ) : (
        <InlineContext.Provider value={inlineApi}>
          <div ref={wrapper} onKeyDown={onKeyDown} data-tour="register">
            {narrow || width < LIST_BELOW ? (
              <MobileTxList
                rows={rows}
                label={singleAccount ? `Transactions for ${accountById.get(singleAccount)?.name ?? "account"}` : "Transactions"}
                empty={emptyState}
                onOpen={openEdit}
                describe={(t) => {
                  const other = t.transferAccountId ? accountById.get(t.transferAccountId) : undefined;
                  const payee = t.transferId
                    ? `Transfer ${t.amountCents < 0 ? "to" : "from"} ${other?.name ?? "another account"}`
                    : ((t.payeeId ? payeeById.get(t.payeeId)?.name : undefined) ?? (t.payeeRaw || "No payee"));
                  const category = t.transferId || t.transferAccountId ? "Transfer" : t.splits.length > 1 ? "Split" : t.splits[0]?.categoryId ? categoryById.get(t.splits[0].categoryId)?.name ?? "Category" : "Uncategorised";
                  return { payee, category };
                }}
              />
            ) : (
              <DataTable
                key={roomy ? "wide" : "medium"}
                label={
                  singleAccount
                    ? `Transactions for ${accountById.get(singleAccount)?.name ?? "account"}`
                    : "Transactions"
                }
                data={rows}
                columns={columns}
                selectable={canEdit}
                rowSelection={selection}
                onRowSelectionChange={setSelection}
                onRowActivate={(t) =>
                  t.id === editingId ? undefined : startInline(t)
                }
                initialVisibility={
                  narrow ? { account: false, category: false } : roomy ? {} : { account: false }
                }
                columnToggle
                maxHeight={embedded ? "60vh" : "70vh"}
                toolbar={
                  <p className="text-xs text-text-muted" aria-live="polite">
                    {total
                      ? `Showing ${rows.length.toLocaleString()} of ${plural(total, "transaction")}`
                      : "No transactions"}
                    <span className="ml-3 hidden lg:inline coarse:!hidden">
                      <Kbd>J</Kbd>/<Kbd>K</Kbd> move, <Kbd>Enter</Kbd> edit,{" "}
                      <Kbd>X</Kbd> select, <Kbd>C</Kbd> categorise, <Kbd>R</Kbd>{" "}
                      reviewed, <Kbd>D</Kbd> delete
                    </span>
                  </p>
                }
                empty={emptyState}
              />
            )}
            {list.hasNextPage ? (
              <div className="mt-3 flex justify-center">
                <Button
                  variant="outline"
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        </InlineContext.Provider>
      )}
      <TransactionEditor
        open={editor.open}
        onOpenChange={(open) => setEditor((s) => ({ ...s, open }))}
        transaction={editor.tx}
        defaults={editor.defaults}
      />
      <CategoriseDialog
        rows={categorising}
        onClose={() => setCategorising(null)}
        onPick={(id, name) => {
          const targets = categorising ?? [];
          setCategorising(null);
          setSelection({});
          void actions.categorise(targets, id, name);
        }}
      />
    </div>
  );
};

/** Phone layout: one tappable row per transaction — payee, date and category on the left, the amount always visible. */
const MobileTxList = ({
  rows,
  label,
  empty,
  onOpen,
  describe,
}: {
  readonly rows: readonly Row[];
  readonly label: string;
  readonly empty: ReactNode;
  readonly onOpen: (t: Row) => void;
  readonly describe: (t: Row) => { payee: string; category: string };
}) => {
  const dates = useDates();
  if (!rows.length) return <>{empty}</>;
  return (
    <ul aria-label={label} className="divide-y divide-border rounded-lg border border-border bg-surface" data-testid="register-list">
      {rows.map((t) => {
        const d = describe(t);
        return (
          <li key={t.id} data-txid={t.id}>
            <button
              type="button"
              onClick={() => onOpen(t)}
              className="flex min-h-touch w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-sunken/60 focus-visible:bg-primary-soft"
            >
              <span className="min-w-0 flex-1">
                <span className={t.isReviewed ? "block truncate text-text" : "block truncate font-semibold text-text"}>{d.payee}</span>
                <span className="block truncate text-xs text-text-muted">
                  {dates.format(t.date, "medium")} · {d.category}
                  {!t.isReviewed ? " · To review" : ""}
                </span>
              </span>
              <MoneyText cents={t.amountCents} tone="auto" className="shrink-0 font-medium" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const CategoriseDialog = ({
  rows,
  onClose,
  onPick,
}: {
  readonly rows: Row[] | null;
  readonly onClose: () => void;
  readonly onPick: (id: string | null, name: string) => void;
}) => {
  const { categories } = useLedger();
  const options = useMemo(() => categoryOptions(categories), [categories]);
  const createCategory = useCreateCategory(rows?.[0] && rows[0].amountCents > 0 ? "income" : "expense");
  return (
    <Dialog open={rows !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="sm"
        title={
          rows && rows.length > 1
            ? `Categorise ${rows.length} transactions`
            : "Categorise"
        }
        description="Pick a category. They're marked reviewed too."
      >
        <Combobox
          aria-label="Category"
          value={null}
          options={options}
          open={rows !== null}
          placeholder="Choose a category"
          onCreate={createCategory}
          onChange={(v, o) => onPick(v, o.label)}
        />
        <Button
          variant="ghost"
          size="sm"
          className="mt-3"
          onClick={() => onPick(null, "Uncategorised")}
        >
          Clear the category
        </Button>
      </DialogContent>
    </Dialog>
  );
};

/** Register widths (px) below which the table drops the Account column, and below which it becomes the list. */
const ACCOUNT_COLUMN_FROM = 840;
const LIST_BELOW = 600;

/** Actions for the selected rows. */
const BulkToolbar = ({
  rows,
  onClear,
  onCategorise,
  onDelete,
}: {
  readonly rows: Row[];
  readonly onClear: () => void;
  readonly onCategorise: () => void;
  readonly onDelete: () => void;
}) => {
  const actions = useTxActions();
  const { tags, openAccounts, entityId } = useLedger();
  const client = useTRPCClient();
  const refresh = useRefresh();
  const from = new Set(rows.map((r) => r.accountId));
  const targets = openAccounts.filter((a) => !from.has(a.id));
  // two halves of one move between accounts that weren't matched automatically
  const [a, b] = rows;
  const pair = rows.length === 2 && a && b && a.accountId !== b.accountId && a.amountCents === -b.amountCents && !a.transferId && !b.transferId
    ? (a.amountCents < 0 ? [a, b] : [b, a]) : null;
  /** "These pay my loan / card": each becomes a transfer to that account; one payee → remembered as a rule. */
  const payTo = async (accountId: string, name: string) => {
    if (!entityId) return;
    try {
      const same = rows.every((r) => r.payeeRaw === rows[0]!.payeeRaw);
      const r = await withLockConfirm((flag) => client.transactions.payToAccount.mutate({ entityId, ids: rows.map((x) => x.id), toAccountId: accountId, remember: same, ...flag }));
      toast.success(`${plural(rows.length, 'payment')} now ${rows.length === 1 ? 'goes' : 'go'} to ${name}${r.rule ? '. Future ones will too' : ''}`);
      onClear();
      void refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary-soft px-3 py-2 shadow-sm"
    >
      <p className="mr-2 text-sm font-medium text-text" aria-live="polite">
        {plural(rows.length, "transaction")} selected
      </p>
      <Button
        size="sm"
        variant="outline"
        icon={<Tag aria-hidden />}
        onClick={onCategorise}
      >
        Categorise
      </Button>
      {tags.length ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" icon={<Hash aria-hidden />}>
              Add tag
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-1">
            <ul aria-label="Tags">
              {tags.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className="flex min-h-10 w-full items-center rounded-md px-2.5 text-left text-sm hover:bg-surface-sunken"
                    onClick={() => void actions.addTag(rows, t.id, t.name)}
                  >
                    #{t.name}
                  </button>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
      {pair && entityId ? (
        <Button
          size="sm"
          variant="outline"
          icon={<ArrowLeftRight aria-hidden />}
          onClick={() => {
            onClear();
            void actions.linkTransfer(entityId, pair[0]!.id, pair[1]!.id);
          }}
        >
          Link as transfer
        </Button>
      ) : null}
      {targets.length && rows.every((r) => !r.transferId && r.splits.length === 1) ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" icon={<ArrowLeftRight aria-hidden />}>
              Pay to account
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-1">
            <p className="px-2.5 py-1.5 text-xs text-text-muted">Loan repayments, card payments and moves between your accounts. That account’s balance changes too.</p>
            <ul aria-label="Accounts">
              {targets.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className="flex min-h-10 w-full items-center rounded-md px-2.5 text-left text-sm hover:bg-surface-sunken"
                    onClick={() => void payTo(a.id, a.name)}
                  >
                    {a.name}
                  </button>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
      <Button
        size="sm"
        variant="outline"
        icon={<CheckCheck aria-hidden />}
        onClick={() => void actions.markReviewed(rows)}
      >
        Mark reviewed
      </Button>
      <Button
        size="sm"
        variant="outline"
        icon={<Check aria-hidden />}
        onClick={() => void actions.setStatus(rows, "cleared")}
      >
        Mark cleared
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="text-negative"
        icon={<Trash2 aria-hidden />}
        onClick={onDelete}
      >
        Delete
      </Button>
      <Button
        size="sm"
        variant="ghost"
        icon={<X aria-hidden />}
        onClick={onClear}
        className="ml-auto"
      >
        Clear selection
      </Button>
    </div>
  );
};
